import React, { useState, useEffect } from 'react';
import { useOrion } from '../context/OrionContext';
import {
  buscarTicket, ticketDaFila, buscarInteracoes,
  nomeOperadorPorCodigo, resolverSolicitantePorTicket,
} from '../services/mysuiteApi';
import { incidentesDoTicket, nomeEmpresaPorCnpj } from '../services/incidentesApi';
import {
  decidirAssumir, liberaFormalizacao, donoDoTicket, STATUS_EM_ATENDIMENTO,
} from '../services/assumirTicket';
import { formatarPrevisao, fmtDataSimples } from '../services/datas';
import { assuntoDoTicket, clienteDoTicket } from '../services/filaRetorno';
import { imagensDoNexus } from '../services/imagensNexus';
import ListaInteracoes from './ListaInteracoes';
import ObservacaoCliente from './ObservacaoCliente';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Resumo do ticket — a primeira parada de qualquer card da tela de início.
//
// Antes, clicar num card já atendia: o ticket era assumido no MySuite e a
// formalização abria, tudo de uma vez. Um clique errado na lista custava um
// ticket marcado em nome de quem não ia atendê-lo.
//
// Agora o clique só MOSTRA. É a mesma leitura que o operador faria abrindo o
// ticket no MySuite — solicitante, cliente, classificação, status, previsão e
// as últimas interações — e as ações ficam aqui dentro, cada uma num botão:
//
//   · Formalizar    → só quando o ticket está em atendimento COM ELE. Escrever
//                     um atendimento alheio é o que a regra evita;
//   · Atender       → assume no MySuite e ATUALIZA ESTE RESUMO. Não navega:
//                     ir para a formalização vira uma segunda escolha;
//   · Ver incidente → só quando existe incidente com o mesmo número de ticket,
//                     o caso comum de quem está no Setor de Sistemas;
//   · Ver todas     → a guia lateral de interações, que já existia.
//
// Quando o ticket é de outro operador não há botão de ação nenhum — só a linha
// dizendo com quem ele está.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Um campo da ficha: ícone à esquerda, rótulo miúdo em cima e o valor embaixo.
 *
 * O ícone não é enfeite — a ficha é uma FAIXA horizontal de seis campos, e sem
 * uma marca visual em cada um eles viram um bloco só de texto pequeno. Vazio
 * vira "—", para a faixa não desalinhar.
 */
function Campo({ icone, rotulo, children, largo = false }) {
  return (
    <div className={`rtk-campo${largo ? ' rtk-campo-largo' : ''}`}>
      <span className="rtk-campo-ic"><Icon name={icone} size={15} /></span>
      <div className="rtk-campo-txt">
        <div className="fld-label">{rotulo}</div>
        <div className="fld-value">{children || '—'}</div>
      </div>
    </div>
  );
}

/**
 * A cor do status, no mesmo vocabulário dos cards da lista.
 *
 * Vai pelo TEXTO porque é o que todas as fontes têm: o código do status não
 * vem em `/tickets/{n}` nem em `/tickets/filtro`.
 */
function classeDoStatus(texto) {
  const t = String(texto || '');
  if (/em\s+atendimento/i.test(t)) return 'st-em_atendimento-pill';
  if (/sistemas/i.test(t)) return 'st-sistemas-pill';
  if (/aguardando/i.test(t)) return 'st-aguardando-pill';
  return 'st-recebida-pill';
}

/**
 * `somenteLeitura` tira TODAS as ações do rodapé, deixando só o "Fechar".
 *
 * Serve à guia de consulta por número, que existe para olhar qualquer ticket —
 * inclusive os de outros setores e de outros colegas. Lá o resumo é uma
 * janela de leitura: quem quer atender vai pela Minha Área ou pelas Filas, onde
 * o ticket aparece no contexto do trabalho e a decisão é deliberada.
 */
export default function ResumoTicket({
  numero, ticketDaLista = null, aoFechar, somenteLeitura = false,
}) {
  const {
    operador, assumirAoAtender, filaRetorno,
    abrirTicketNaFormalizacao, abrirIncidenteDoTicket,
  } = useOrion();

  const [carregando, setCarregando] = useState(true);
  const [detalhe, setDetalhe] = useState(null);
  const [pendencia, setPendencia] = useState(null);
  const [solicitante, setSolicitante] = useState('');
  const [nomeDono, setNomeDono] = useState('');
  const [temIncidente, setTemIncidente] = useState(false);
  const [interacoes, setInteracoes] = useState([]);
  const [assumindo, setAssumindo] = useState(false);
  // O nome do cliente, quando só o CNPJ está disponível (ver o comentário
  // junto da busca, mais abaixo).
  const [nomeViaCnpj, setNomeViaCnpj] = useState('');
  // Prints que o Setor de Sistemas anexou no Nexus e que a API do MySuite não
  // deixa subir para o ticket — ver services/imagensNexus.js.
  const [imagensNexus, setImagensNexus] = useState([]);
  const [ampliada, setAmpliada] = useState(null);
  // Qual aba da área de baixo está aberta: 'interacoes' ou 'anexos'. Abre
  // sempre na conversa, que é o que se lê primeiro.
  const [aba, setAba] = useState('interacoes');

  // Tudo o que o resumo mostra vem de fontes diferentes, e nenhuma delas
  // depende da outra para começar: o detalhe do ticket, o registro na fila de
  // pendentes (o único que traz o DONO), o solicitante, as interações e a
  // existência de incidente. Por isso saem juntas — em série, o resumo levaria
  // a soma dos tempos para pintar.
  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setImagensNexus([]);
    setAba('interacoes');

    // Fora do Promise.all de baixo de propósito: é outra fonte (o Supabase do
    // Nexus), e o resumo não deve esperar por ela para pintar.
    imagensDoNexus(numero).then(lista => { if (vivo) setImagensNexus(lista); });

    (async () => {
      const [det, pend] = await Promise.all([
        buscarTicket(numero).catch(() => null),
        ticketDaFila(numero).catch(() => null),
      ]);
      if (!vivo) return;
      setDetalhe(det);
      setPendencia(pend);

      // O dono sai do detalhe, que é a leitura mais fresca e responde por
      // qualquer ticket; a fila de pendentes e o registro da lista ficam de
      // reserva, para quando `/tickets/{n}` não trouxer o campo.
      const dono = donoDoTicket(det) ?? donoDoTicket(pend || ticketDaLista);

      // O NOME do cliente só existe quando o MySuite o traz pronto na lista
      // (`clienteDoTicket`, mais abaixo). Quando não traz — o caso deste
      // ticket —, a única coisa que sobra é o CNPJ, e mostrar um CNPJ cru no
      // lugar do nome do cliente é pior do que gastar uma consulta a mais.
      // `pulse.empresas_config` é o cadastro de clientes da Space por CNPJ, e
      // é a mesma fonte que o lote de tickets em massa já usa — ver
      // `nomeEmpresaPorCnpj` em services/incidentesApi.js. Só busca quando o
      // nome falta E há um CNPJ para consultar; ticket sem os dois não gera
      // consulta nenhuma.
      const cnpj = det?.cnpj || det?.cnpjCliente || ticketDaLista?.cnpjCliente || pend?.cnpjCliente || '';
      const precisaNomeViaCnpj = !clienteDoTicket(ticketDaLista || {}) && !!cnpj;

      const [nome, quem, incs, inter, nomeCnpj] = await Promise.all([
        resolverSolicitantePorTicket(numero).catch(() => ''),
        dono ? nomeOperadorPorCodigo(dono).catch(() => '') : Promise.resolve(''),
        incidentesDoTicket(numero).catch(() => []),
        buscarInteracoes(numero, det?.primeiraInteracao).catch(() => []),
        precisaNomeViaCnpj ? nomeEmpresaPorCnpj(cnpj).catch(() => '') : Promise.resolve(''),
      ]);
      if (!vivo) return;

      setSolicitante(nome);
      setNomeDono(quem);
      setTemIncidente(incs.length > 0);
      setInteracoes(inter || []);
      setNomeViaCnpj(nomeCnpj);
      setCarregando(false);
    })();

    return () => { vivo = false; };
    // Só o número dispara nova consulta. `ticketDaLista` é o retrato que veio
    // do card: se a fila recarregar por baixo, o resumo aberto não deve piscar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero]);

  // Esc fecha — o mesmo caminho de saída dos outros modais do Orion. A imagem
  // ampliada de um anexo tem Esc próprio, dentro da lista de interações.
  useEffect(() => {
    // Com uma imagem do Nexus ampliada, o Esc fecha só ela.
    const aoTeclar = (e) => {
      if (e.key !== 'Escape') return;
      if (ampliada) setAmpliada(null);
      else aoFechar?.();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aoFechar, ampliada]);

  // A fonte da decisão junta as três leituras, cada uma boa em algo:
  //
  //   · o item da lista (`/tickets/filtro`) traz dono e status como TEXTO, e é
  //     o único lugar onde um ticket no Setor de Sistemas aparece;
  //   · a fila de pendentes traz o dono com o CÓDIGO do status;
  //   · o detalhe do ticket é a leitura mais fresca do status E DO DONO —
  //     quando ele responde, o código das outras fontes sai da conta, senão uma
  //     fila atrasada mandaria em cima da informação atual.
  //
  // O DONO do detalhe passou a valer aqui porque `/tickets/{n}` devolve
  // `operador` (conferido na API: o ticket 348745 volta com `operador: 115` ao
  // lado de "SUP - Em Atendimento"). Sem ele, um ticket em atendimento com o
  // próprio operador ficava SEM dono legível toda vez que o resumo abria fora
  // de um card da lista — pelo histórico, pela fila de retorno, por número — ou
  // quando a fila de pendentes ainda dizia 106. Sem dono, a regra entendia
  // "ticket solto" e oferecia **Atender** um ticket que já era dele.
  const donoDoDetalhe = Number(detalhe?.operador);
  const fonte = (pendencia || ticketDaLista || detalhe)
    ? {
      ...(ticketDaLista || {}),
      ...(pendencia || {}),
      ...(detalhe?.status
        ? { status: detalhe.status, codigoStatusTicket: undefined, codigoStatus: undefined }
        : {}),
      // Os dois nomes do mesmo campo vão juntos: `donoDoTicket` lê
      // `codigoOperador` primeiro, e deixar o antigo para trás faria a fila
      // atrasada continuar mandando.
      ...(Number.isFinite(donoDoDetalhe)
        ? { codigoOperador: donoDoDetalhe, operador: donoDoDetalhe }
        : {}),
    }
    : null;

  // Quem decide o que este resumo oferece é a MESMA regra do "Atender": não há
  // uma segunda leitura de quem pode o quê.
  const { pode, motivo } = decidirAssumir(fonte, operador?.codigo);
  const emAtendimentoComigo = liberaFormalizacao(motivo) && !pode;
  const deOutro = motivo === 'de-outro' || motivo === 'ja-de-outro';
  // Nome primeiro, sempre: o que o MySuite já traz pronto, senão o que
  // `pulse.empresas_config` resolveu a partir do CNPJ. O CNPJ cru só aparece
  // quando nenhuma das duas fontes tem o cliente cadastrado — melhor um
  // código do que um campo em branco.
  const cliente = clienteDoTicket(ticketDaLista || {}) || nomeViaCnpj
    || detalhe?.cnpj || detalhe?.cnpjCliente || '';
  const assunto = detalhe?.titulo || assuntoDoTicket(ticketDaLista || {});

  const atender = async () => {
    setAssumindo(true);
    let deuCerto = false;
    try {
      deuCerto = await assumirAoAtender(numero, fonte);
    } finally {
      setAssumindo(false);
    }
    if (!deuCerto) return;

    // O resumo se atualiza no lugar. Reconsultar não adiantaria: a fila de
    // pendentes leva alguns minutos para mostrar o novo status, e o resumo
    // voltaria dizendo que o ticket continua onde estava.
    setPendencia(p => ({
      ...(p || {}),
      codigoStatusTicket: STATUS_EM_ATENDIMENTO,
      codigoOperador: Number(operador?.codigo),
    }));
    setDetalhe(d => (d ? { ...d, status: 'SUP - Em Atendimento' } : d));
    setNomeDono(operador?.nome || '');

    // Um retorno assumido está tratado: o aviso da hora marcada não deve mais
    // soar por ele. Vale aqui e no "Formalizar" — as duas são o momento em que
    // o operador de fato pegou o ticket.
    filaRetorno?.marcarTratado?.(numero);
  };

  const formalizar = () => {
    filaRetorno?.marcarTratado?.(numero);
    aoFechar?.();
    abrirTicketNaFormalizacao(numero);
  };

  const verIncidente = () => {
    aoFechar?.();
    abrirIncidenteDoTicket(numero);
  };

  return (
    <>
      <div className="drawer-overlay" onClick={aoFechar}>
        <div className="modal-nx modal-largo modal-resumo" onClick={e => e.stopPropagation()}>
          {/* O NÚMERO é o que identifica o ticket em toda conversa da equipe —
              é por ele que se procura no MySuite, é ele que vai no chat. Por
              isso ele é o título, em corpo grande, com o cliente logo abaixo. */}
          <div className="drawer-head rtk-head">
            <div className="rtk-head-txt">
              <div className="rtk-numero">Ticket nº {numero}</div>
              <div className="rtk-cliente">{cliente || 'resumo do ticket'}</div>
            </div>
            <button className="drawer-close" onClick={aoFechar}><Icon name="x" size={15} /></button>
          </div>

          {/* DUAS FAIXAS, e não uma rolagem só. A ficha do ticket é consulta —
              olha-se uma vez e a informação não muda —, enquanto a conversa é o
              que o operador realmente lê. Por isso a ficha é presa em cerca de
              30% da altura (rolando por dentro quando não cabe) e as interações
              ficam com o resto. Antes as classificações empurravam a conversa
              para fora da tela e era preciso rolar o modal inteiro para achá-la. */}
          <div className="drawer-body rt-corpo">
            {carregando && !detalhe ? (
              <div className="campo-dica">
                <Icon name="rotate-cw" size={13} className="orion-spin" /> Consultando o MySuite…
              </div>
            ) : (
              <>
                {/* ONDE O TICKET ESTÁ. Fica fora da caixa da ficha, na faixa
                    logo abaixo do número: status, setor e categoria são a
                    primeira pergunta de quem abre o resumo, e não devem
                    disputar espaço com os campos. */}
                <div className="rtk-faixa">
                  <span className={`pill ${classeDoStatus(fonte?.status)}`}>
                    {fonte?.status || 'Status desconhecido'}
                  </span>
                  {detalhe?.setor && <span className="rtk-setor">{detalhe.setor}</span>}
                  {detalhe?.categoria && <span className="rtk-categoria">{detalhe.categoria.trim()}</span>}
                </div>

                <div className="rt-ficha">
                  {/* PRIMEIRA LINHA — as seis identidades do ticket: com quem
                      ele fala (solicitante, cliente), quem o atende e como ele
                      foi classificado. É o que se lê de relance. */}
                  <div className="rtk-campos">
                    <Campo icone="user" rotulo="Solicitante">{solicitante}</Campo>
                    <Campo icone="building" rotulo="Cliente">{cliente}</Campo>
                    <Campo icone="users" rotulo="Operador">
                      {nomeDono || (donoDoTicket(fonte) ? `Operador ${donoDoTicket(fonte)}` : '')}
                    </Campo>
                    <Campo icone="alert-triangle" rotulo="Ocorrência">{detalhe?.ocorrencia}</Campo>
                    <Campo icone="bar-chart" rotulo="Criticidade">{detalhe?.criticidade}</Campo>
                    <Campo icone="tag" rotulo="Classificação">{detalhe?.classificacao}</Campo>
                  </div>

                  {/* SEGUNDA LINHA — o que situa o ticket no tempo, mais o
                      assunto. Continua aqui porque a previsão é compromisso com
                      o cliente e some da tela em lugar nenhum; separada da
                      primeira porque não é identidade, é contexto. */}
                  <div className="rtk-campos rtk-campos-sec">
                    <Campo icone="clock" rotulo="Previsão">
                      {formatarPrevisao(detalhe?.dataPrevisao, detalhe?.horaPrevisao)}
                    </Campo>
                    <Campo icone="history" rotulo="Aberto em">{fmtDataSimples(pendencia?.dataAbertura)}</Campo>
                    <Campo icone="file-text" rotulo="Assunto" largo>{assunto}</Campo>
                  </div>
                </div>

                <ObservacaoCliente
                  cnpj={detalhe?.cnpj || detalhe?.cnpjCliente || ticketDaLista?.cnpjCliente || pendencia?.cnpjCliente}
                />

                {/* De quem é o ticket, quando não é de quem está olhando. Sem
                    isto o resumo simplesmente não teria botão de ação, e o
                    motivo ficaria por conta da adivinhação.
                    FORA da ficha de propósito: dentro dela, que rola por dentro,
                    o aviso poderia ficar escondido justamente para quem precisa
                    lê-lo. */}
                {deOutro && (
                  <div className="rt-aviso">
                    <Icon name="alert-circle" size={13} />
                    Este ticket está com {nomeDono || `o operador ${donoDoTicket(fonte)}`}.
                    Fale com quem está atendendo antes de assumir.
                  </div>
                )}

                {/* A conversa INTEIRA, aqui dentro. Era uma guia lateral que
                    deslizava por cima da tela; agora é parte do resumo, que é
                    onde o operador já está olhando o ticket — e é ela que fica
                    com a maior parte do espaço.
                    As imagens do Nexus dividem o MESMO espaço, numa aba: como
                    seção própria, empilhada acima, elas empurravam a conversa
                    para fora da tela em monitor pequeno. A aba só aparece
                    quando o ticket tem imagem no Nexus. */}
                <div className="drawer-section rt-conversa">
                  {imagensNexus.length > 0 ? (
                    <div className="rtk-abas" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={aba === 'interacoes'}
                        className={`rtk-aba${aba === 'interacoes' ? ' ativa' : ''}`}
                        onClick={() => setAba('interacoes')}
                      >
                        <Icon name="message-square" size={13} /> Interações
                        {interacoes.length > 0 && <span className="rtk-aba-cnt">{interacoes.length}</span>}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={aba === 'anexos'}
                        className={`rtk-aba${aba === 'anexos' ? ' ativa' : ''}`}
                        onClick={() => setAba('anexos')}
                      >
                        <Icon name="image" size={13} /> Anexos do Nexus
                        <span className="rtk-aba-cnt">{imagensNexus.length}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="drawer-section-title rtk-titulo">
                      Interações
                      {interacoes.length > 0 && (
                        <span className="rtk-contagem">
                          {interacoes.length === 1 ? '1 registro' : `${interacoes.length} registros`}
                        </span>
                      )}
                    </div>
                  )}

                  {aba === 'anexos' && imagensNexus.length > 0 ? (
                    <div className="rtk-imagens">
                      {imagensNexus.map(img => (
                        <button
                          type="button"
                          key={img.id}
                          className="rtk-imagem"
                          onClick={() => setAmpliada(img.url)}
                          title={`${img.origem} — clique para ampliar`}
                        >
                          <img src={img.url} alt={img.nome || 'Imagem do Nexus'} loading="lazy" />
                          <span className="rtk-imagem-origem">{img.origem}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <ListaInteracoes
                      interacoes={interacoes}
                      carregando={carregando}
                      solicitante={solicitante}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Em modo consulta o rodapé tem só a saída: nenhuma ação do ticket
              aparece, então não há como assumir por engano o ticket de um
              colega que se foi apenas conferir. */}
          <div className="drawer-foot">
            <button className="btn" onClick={aoFechar}>Fechar</button>

            {!somenteLeitura && temIncidente && (
              <button className="btn btn-ghost" onClick={verIncidente}>
                <Icon name="alert-triangle" size={13} /> Ver incidente
              </button>
            )}

            {!somenteLeitura && emAtendimentoComigo && (
              <button className="btn btn-primary" onClick={formalizar}>
                <Icon name="pen" size={13} /> Formalizar
              </button>
            )}

            {!somenteLeitura && pode && (
              <button className="btn btn-primary" onClick={atender} disabled={assumindo}>
                {assumindo
                  ? <><Icon name="rotate-cw" size={13} className="orion-spin" /> Assumindo…</>
                  : <><Icon name="ticket" size={13} /> Atender</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Por cima do resumo, como a ampliação de anexo da lista de interações. */}
      {ampliada && (
        <div className="lightbox" onClick={() => setAmpliada(null)}>
          <img src={ampliada} alt="Imagem do Nexus ampliada" />
        </div>
      )}
    </>
  );
}
